import { match, P } from 'ts-pattern';
import { BrandAffinityLevel } from 'modules/domains';
import {
  AFFINITY_TRENDS_DELTA_PERCENT_TEXT,
  AFFINITY_TRENDS_TEXTS_BY_DIRECTION,
} from 'modules/intelligentInsights/brandAffinityInsights/constants';
import {
  DeltaItem,
  DeltaDirectionEnum,
  TrendTypeEnum,
} from 'modules/intelligentInsights/brandAffinityInsights/types';
import {
  formatCsvFilenameWithDate,
  formatCount,
  formatCountWithSign,
  formatPercentagePoints,
  formatPercentWithSign,
} from 'modules/intelligentInsights/domain.utils';
import { TimespanEnum } from 'modules/intelligentInsights/types';

export const formatDeltaTextContent = (trendType: TrendTypeEnum, deltaItemText: string) =>
  trendType === TrendTypeEnum.Percent
    ? `${deltaItemText} ${AFFINITY_TRENDS_DELTA_PERCENT_TEXT}`
    : deltaItemText;

export const getTrendProperties = (
  trendType: TrendTypeEnum,
  deltaItem: DeltaItem,
  direction: DeltaDirectionEnum,
  brandAffinityText: string,
) =>
  match({ trendType, direction })
    .with(
      { trendType: TrendTypeEnum.Percent, direction: P.not(DeltaDirectionEnum.unscored) },
      () => {
        const { title, movement, dataThresholdText } =
          AFFINITY_TRENDS_TEXTS_BY_DIRECTION[direction];
        return {
          dataThresholdText: `No cohort had a ${dataThresholdText} in users`,
          label: deltaItem.brandAffinityLabel,
          movementText: `${movement} ${brandAffinityText} users`,
          title,
          valueText: formatPercentWithSign(deltaItem.percent),
        };
      },
    )
    .with({ trendType: TrendTypeEnum.Count, direction: P.not(DeltaDirectionEnum.unscored) }, () => {
      const { title, movement, dataThresholdText } = AFFINITY_TRENDS_TEXTS_BY_DIRECTION[direction];
      return {
        dataThresholdText: `No cohort had a ${dataThresholdText} in users`,
        label: deltaItem.brandAffinityLabel,
        movementText: `${movement} ${brandAffinityText} users`,
        title,
        valueText: formatCountWithSign(deltaItem.delta),
      };
    })
    .with({ trendType: TrendTypeEnum.Percent, direction: DeltaDirectionEnum.unscored }, () => {
      const { title, movement } = AFFINITY_TRENDS_TEXTS_BY_DIRECTION[direction];
      return {
        dataThresholdText: '',
        label: deltaItem.brandAffinityLabel,
        movementText: `${movement} ${brandAffinityText}`,
        title,
        valueText: `${formatPercentagePoints(deltaItem.percent)}%`,
      };
    })
    .with({ trendType: TrendTypeEnum.Count, direction: DeltaDirectionEnum.unscored }, () => {
      const { title, movement } = AFFINITY_TRENDS_TEXTS_BY_DIRECTION[direction];
      return {
        dataThresholdText: '',
        label: deltaItem.brandAffinityLabel,
        movementText: `${movement} ${brandAffinityText}`,
        title,
        valueText: formatCount(deltaItem.delta),
      };
    })
    .otherwise(() => ({
      dataThresholdText: '',
      label: BrandAffinityLevel.UNSCORED,
      movementText: '',
      title: '',
      valueText: '',
    }));

export const createFileNameFormatter =
  (trendType: TrendTypeEnum, timespan: TimespanEnum) => (formattedDate: string) =>
    `User_affinity_trends_${trendType.toLocaleLowerCase()}_${timespan}_days_${formattedDate}.csv`;

export const createCSVFilename = (trendType: TrendTypeEnum, selectedTimespan: TimespanEnum) =>
  formatCsvFilenameWithDate(createFileNameFormatter(trendType, selectedTimespan));
